import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Home, Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react'
import { onPlatformAuth, logInEmail, logOutPlatform } from '../lib/platformAuth.js'
import { watchAccount, isActive } from '../lib/platformAccount.js'
import { openBillingPortal } from '../lib/platformBilling.js'
import { setActiveClubId } from '../lib/tenant.js'
import { isOwnerEmail } from '../config/owners.js'
import { GeGlassStyles, heroBg, heroLogo } from './geGlass.jsx'

// Gates the Golf Elite club-admin surface: must be signed in AND hold an active
// subscription. Active → sets the tenant clubId and renders the app. Signed out
// → login. Signed in but past_due/canceled → "update billing". Public golfer
// pages are NOT wrapped by this (they resolve clubId from the link).
export default function GolfEliteGate({ children }) {
  const [user, setUser] = useState(undefined) // undefined = loading
  const [account, setAccount] = useState(undefined)

  useEffect(() => onPlatformAuth(setUser), [])
  useEffect(() => {
    if (!user) { setAccount(user === null ? null : undefined); return }
    return watchAccount(user.uid, setAccount)
  }, [user])

  // OWNER LOCK: an allowlisted owner email always gets full, lifetime access —
  // never the subscription/billing gate, never "update billing".
  const owner = isOwnerEmail(user?.email)
  const active = owner || isActive(account)
  useEffect(() => { setActiveClubId(active ? (account?.clubId || null) : null) }, [active, account])

  if (user === undefined) return <Centered>Loading…</Centered>
  if (!user) return <LoginScreen />
  // Owners go straight in — don't wait on an account or a subscription.
  if (owner) return children
  if (account === undefined) return <Centered>Loading…</Centered>
  if (!active) return <BillingRestricted account={account} />
  return children
}

// Shared glass/wave shell — same look as /signup (GolfEliteSignup): full-bleed
// hero background, glass logo + glass Back/Home, frosted-glass card.
function Shell({ children, width = 420 }) {
  const navigate = useNavigate()
  return (
    <main className="ge-page" style={{ backgroundImage: `url(${heroBg})` }}>
      <GeGlassStyles />
      <header className="ge-hero">
        <nav className="ge-hero-nav">
          <img className="ge-logo" src={heroLogo} alt="Golf Elite" />
          <button type="button" className="ge-nav-btn" onClick={() => navigate(-1)}><ArrowLeft size={16} strokeWidth={2.4} /><span>Back</span></button>
          <button type="button" className="ge-nav-btn" onClick={() => navigate('/')}><Home size={16} strokeWidth={2.4} /><span>Home</span></button>
        </nav>
      </header>
      <section className="ge-content">
        <div className="ge-shell" style={{ width: `min(${width}px, calc(100vw - 32px))` }}>{children}</div>
      </section>
    </main>
  )
}

function Field({ id, label, icon, trailing, children }) {
  return (
    <div className="ge-group">
      <label htmlFor={id}>{label}</label>
      <div className="ge-field-wrap">
        <span className="ge-field-icon">{icon}</span>
        {children}
        {trailing || null}
      </div>
    </div>
  )
}

function LoginScreen() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true)
    try { await logInEmail(email.trim(), password) }
    catch { setErr('Incorrect email or password.'); setBusy(false) }
  }
  return (
    <Shell>
      <h1 className="ge-auth-title">Log in</h1>
      <form className="ge-form" onSubmit={submit}>
        <Field id="login-email" label="EMAIL" icon={<Mail size={18} />}>
          <input id="login-email" className="ge-field" type="email" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" />
        </Field>
        <Field id="login-pw" label="PASSWORD" icon={<Lock size={18} />} trailing={
          <button type="button" className="ge-eye" onClick={() => setShowPw((s) => !s)} aria-label="Show password">
            {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        }>
          <input id="login-pw" className="ge-field" type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" />
        </Field>
        {err ? <p className="ge-err">{err}</p> : null}
        <button type="submit" className="ge-start" disabled={busy}>
          <span>{busy ? 'SIGNING IN…' : 'LOG IN'}</span>
          {busy ? null : <ArrowRight size={22} strokeWidth={2.4} />}
        </button>
      </form>
      <p className="ge-auth-link">New club? <Link to="/signup">Start now</Link></p>
    </Shell>
  )
}

function BillingRestricted({ account }) {
  const [busy, setBusy] = useState(false)
  const status = account?.status || 'inactive'
  return (
    <Shell>
      <h1 className="ge-auth-title">Subscription {status}</h1>
      <button
        type="button"
        disabled={busy}
        onClick={async () => { setBusy(true); try { await openBillingPortal() } catch { setBusy(false) } }}
        className="ge-start">
        <span>{busy ? 'OPENING…' : 'UPDATE BILLING'}</span>
      </button>
      <button type="button" onClick={() => logOutPlatform()} className="ge-auth-logout">Log out</button>
    </Shell>
  )
}

function Centered({ children }) {
  return (
    <main className="ge-page" style={{ backgroundImage: `url(${heroBg})`, display: 'grid', placeItems: 'center', color: '#fff' }}>
      <GeGlassStyles />
      <div style={{ fontWeight: 700, fontSize: 16 }}>{children}</div>
    </main>
  )
}

// Manage-billing opener (Stripe Customer Portal — switch plan, update card,
// cancel). Reached at /billing; wrapped by the gate so it's authenticated.
export function ManageBilling() {
  const [err, setErr] = useState('')
  useEffect(() => { openBillingPortal().catch((e) => setErr(e?.message || 'Billing portal unavailable.')) }, [])
  return (
    <Shell>
      <h1 className="ge-auth-title">Opening billing…</h1>
      {err ? <p className="ge-err" style={{ textAlign: 'center' }}>{err}</p> : null}
      <p className="ge-auth-link"><Link to="/admin">Back to admin</Link></p>
    </Shell>
  )
}
